// src/components/BuySidebar.tsx
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion";
import tokenImage from "../../assets/img/Token_NAU.webp";
import tokenImageY from "../../assets/img/Token_yes.webp";
import tokenImageN from "../../assets/img/Token_no.webp";
import Logo from "../../assets/img/logo.webp";
import FollowAs from "../footer/FollowAs";
import walletClose from "../../assets/img/uniswapClos.png";
import walletOpen from "../../assets/img/uniswapOpen.png";

const tokenButtons = [
  {
    label: "NAU",
    link: "https://app.uniswap.org/explore/pools/base/0x8BD48c8C8f99cBe32b117741ca9C074c4A20Cd98",
    icon: tokenImage,
  },
  {
    label: "NAUY",
    link: "https://app.uniswap.org/explore/pools/base/0x9DB95E2273cb8803Adb25D63E9E4342FF4547c0A",
    icon: tokenImageY,
  },
  {
    label: "NAUN",
    link: "https://app.uniswap.org/explore/pools/base/0x51CB08aa46ABE7F571260eB5BaC7fa4AB7B35e8d",
    icon: tokenImageN,
  },
];

const sidebarVariants: Variants = {
  open: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  closed: {
    x: "100%",
    opacity: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      when: "afterChildren",
    },
  },
};

const itemVariants: Variants = {
  open: {
    y: 0,
    opacity: 1,
    transition: { y: { stiffness: 1000, velocity: -100 } },
  },
  closed: {
    y: 20,
    opacity: 0,
    transition: { y: { stiffness: 1000 } },
  },
};

const BuySidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleGlobalClose = () => {
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("closeAllSidebars", handleGlobalClose);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("closeAllSidebars", handleGlobalClose);
    };
  }, []);

  const handleToggle = () => {
    window.dispatchEvent(new Event("closeAllSidebars"));
    setIsOpen((prev) => !prev);
  };

  return (
    <>
      <button
        onClick={handleToggle}
        aria-label="Buy Our Tokens"
        className="relative flex items-center group"
      >
        <img
          src={walletClose}
          alt="Closed wallet icon"
          className="w-8 h-8 transition-all duration-500 ease-in-out group-hover:opacity-0 group-hover:scale-110 mb-4"
        />
        <img
          src={walletOpen}
          alt="Open wallet icon"
          className="absolute w-8 h-8 opacity-0 group-hover:opacity-100 scale-110 rotate-6 transition-all duration-500 ease-in-out"
        />
        <p className="ml-1 text-base sm:text-[16px] font-extrabold text-red-200 relative group-hover:text-[var(--color-primary)] transition-colors duration-300 after:absolute after:bottom-[-4px] after:left-0 after:w-0 group-hover:after:w-full after:h-[2px] after:bg-[var(--color-primary)] after:transition-all after:duration-500 after:ease-in-out">
          Buy Our Tokens on UNISWAP
        </p>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.aside
            ref={sidebarRef}
            initial="closed"
            animate="open"
            exit="closed"
            variants={sidebarVariants}
            className="absolute top-20 right-0 z-40 w-90 bg-[#031B34] text-white shadow-2xl overflow-hidden"
          >
            <div className="flex flex-col h-full p-6">
              <div className="flex items-center justify-between">
                <motion.div variants={itemVariants} className="pb-4">
                  <h3 className="text-2xl font-bold">Buy our Tokens</h3>
                  <p className="text-sm text-white/60">UC Waves</p>
                </motion.div>
                <img src={Logo} alt="logoUCW" width={100} />
              </div>

              <motion.div
                variants={itemVariants}
                className="mt-6 flex-1 flex flex-col items-center justify-center space-y-4"
              >
                <p className="text-lg my-2 text-white">
                  Will be active after the presale
                </p>
                {tokenButtons.map(
                  ({
                    label,
                    //  link,
                    icon,
                  }) => (
                    <motion.a
                      key={label}
                      // href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center w-[40%] p-3 bg-white/20 rounded-full ring-1 ring-white/20 hover:bg-white/10 transition"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      variants={itemVariants}
                    >
                      <img
                        src={icon}
                        alt={label}
                        className="w-8 h-8 mr-4 rounded-full"
                      />
                      <span className="text-lg font-medium">{label}</span>
                    </motion.a>
                  )
                )}

                <motion.div variants={itemVariants}>
                  <FollowAs />
                </motion.div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="mt-auto text-center text-xs text-white/40 pb-4"
              >
                Powered by NextGenWaves
              </motion.div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

export default BuySidebar;
